import * as autobind from 'autobind';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'redux-scaffolding-ts';
import { Grid, Icon, Message } from 'semantic-ui-react';
import { NMTrainingNamesStore, NMTrainingNameDto } from 'stores/configuration/events-n-requests/non-machine-related/training-names-store';
import { ItemState, Query, OrderDefinition } from 'stores/dataStore';
import { CommandResult } from 'stores/types';
import { nameof } from 'utils/object';
import { TableModel, TableView } from 'widgets/collections/table';
import { TextBoxFilter } from 'widgets/collections/table-filters/textbox-filter';
import NewNMTrainingNameView from './new-nm-training-name';
import ChangeNMTrainingNameView from './edit-nm-training-name';
import { OptionsFilter } from 'widgets/collections/table-filters/option-filter';
import { Categories } from 'stores/requests/requests-store';

export interface NMTrainingNameListProps extends WithTranslation, RouteComponentProps {
  nmTrainingNames: NMTrainingNamesStore;
}

export interface NMTrainingNameListState {
  query: Query;
  newNMTrainingNameShown: boolean;
  changeNMTrainingNameShown: boolean;
  activeFilters: string[];
  selectedItem: any;
  someFilterOpened: boolean;
}

@connect(['nmTrainingNames', NMTrainingNamesStore])
class NMTrainingNameListPage extends React.Component<NMTrainingNameListProps, NMTrainingNameListState> {
  handleOnEnterKeydown = item => {
    this.setState({ selectedItem: item }, () => this.onEditItem());
  };

  handleOnRowDoubleClick = item => {
    this.handleOnEnterKeydown(item);
  };

  constructor(props) {
    super(props);
    this.state = {
      query: { searchQuery: '', orderBy: [{ direction: 'Descending', field: 'modifiedOn', useProfile: false }], skip: 0, take: 10 },
      newNMTrainingNameShown: false,
      changeNMTrainingNameShown: false,
      activeFilters: [],
      selectedItem: null,
      someFilterOpened: false
    };
  }

  componentDidMount() {
    this.load();
  }

  @autobind
  private load() {
    this.props.nmTrainingNames.getAllAsync(this.state.query);
  }

  @autobind
  private handleOrderBy(orderBy: OrderDefinition[]) {
    this.setState(
      {
        query: Object.assign(this.state.query, {
          orderBy: [...orderBy, { direction: 'Descending', field: 'modifiedOn', useProfile: false }]
        })
      },
      this.load
    );
  }

  @autobind
  private handlePageChange(skip: number, take: number) {
    this.setState({ query: Object.assign(this.state.query, { skip, take }) });
    this.load();
  }

  @autobind
  private async onSaveRow(item: NMTrainingNameDto, state: ItemState): Promise<CommandResult<any>> {
    if (state !== 'New') {
      await this.props.nmTrainingNames.saveAsync(item, state);
    }
    return { isSuccess: true, items: [], messages: [] };
  }

  @autobind
  private async onDelete(item: NMTrainingNameDto, state: ItemState): Promise<CommandResult<any>> {
    if (state !== 'New') {
      await this.props.nmTrainingNames.deleteAsync(item.id, state);
    }
    return { isSuccess: true, items: [], messages: [] };
  }

  @autobind
  private handleFilterChange(filters: { id: string; filter: any }[]) {
    const filter = filters.map(f => f.filter);
    const activeFilters = filters.map(f => f.id);

    const query = Object.assign(this.state.query, { filter, skip: 0 });
    this.setState({ query, activeFilters }, () => this.load());
  }

  @autobind
  private onNewItem() {
    this.setState({ newNMTrainingNameShown: true });
  }

  @autobind
  private onNewItemClosed(isSuccess: boolean) {
    this.setState({ newNMTrainingNameShown: false });
    if (isSuccess) this.load();
  }

  @autobind
  private onEditItem() {
    this.setState({ changeNMTrainingNameShown: true });
  }

  @autobind
  private onEditItemClosed(isSuccess: boolean) {
    this.setState({ changeNMTrainingNameShown: false });
    if (isSuccess) this.load();
  }

  handleOnActivateFilter = (visible: boolean) => {
    this.setState({ someFilterOpened: visible });
  };

  public render() {
    const { t } = this.props as any;
    const { activeFilters, someFilterOpened } = this.state;

    const tableModel = {
      columns: [
        {
          title: t('Name'),
          tooltipRenderer: true,
          newClassFromComponent: data => 'table__more-text-long',
          renderer: data => data.name,
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by name')}
              triggerTitle={title}
              onFilter={value =>
                onFilter(
                  nameof<NMTrainingNameDto>('name'),
                  `contains(tolower(${nameof<NMTrainingNameDto>('name')}), '${value.toLowerCase()}')`
                )
              }
              onClear={() => onClear(nameof<NMTrainingNameDto>('name'))}
              active={activeFilters.includes(nameof<NMTrainingNameDto>('name'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: nameof<NMTrainingNameDto>('name'),
            useProfile: false
          }
        },
        {
          title: t('Cluster'),
          renderer: data => <>{data.clusterName}</>,
          tooltipRenderer: true,
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by Cluster')}
              triggerTitle={title}
              onFilter={value =>
                onFilter(nameof<NMTrainingNameDto>('clusterName'), `contains(FunctionalArea/Cluster/Name, '${value.toLowerCase()}')`)
              }
              onClear={() => onClear(nameof<NMTrainingNameDto>('clusterName'))}
              active={activeFilters.includes(nameof<NMTrainingNameDto>('clusterName'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: 'FunctionalArea/Cluster/Name',
            useProfile: false
          }
        },
        {
          title: t('Functional Area'),
          tooltipRenderer: true,
          newClassFromComponent: data => 'table__more-text-long',
          renderer: data => data.functionalAreaName,
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by functional area')}
              triggerTitle={title}
              onFilter={value =>
                onFilter(nameof<NMTrainingNameDto>('functionalAreaName'), `contains(FunctionalArea/Name, '${value.toLowerCase()}')`)
              }
              onClear={() => onClear(nameof<NMTrainingNameDto>('functionalAreaName'))}
              active={activeFilters.includes(nameof<NMTrainingNameDto>('functionalAreaName'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: 'FunctionalArea/Name',
            useProfile: false
          }
        },
        {
          title: t('Category'),
          tooltipRenderer: true,
          newClassFromComponent: data => 'table__more-text-long',
          renderer: data => (Categories[data.category] ? data.category : ''),
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <OptionsFilter
              filterTitle={t('Filter by category')}
              options={[
                { text: t('Technical'), value: 'Technical' },
                { text: t('Functional'), value: 'Functional' }
              ]}
              triggerTitle={title}
              onFilter={value => onFilter(nameof<NMTrainingNameDto>('category'), { FunctionalArea: { Cluster: { Category: value } } })}
              onClear={() => onClear(nameof<NMTrainingNameDto>('category'))}
              active={activeFilters.includes(nameof<NMTrainingNameDto>('category'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: { field: 'FunctionalArea/Cluster/Category', useProfile: false }
        }
      ],
      data: this.props.nmTrainingNames.state
    } as TableModel<NMTrainingNameDto>;
    return (
      <>
        <Grid className="event-types-list-grid">
          {this.props.nmTrainingNames.state.result && !this.props.nmTrainingNames.state.result.isSuccess && (
            <Grid.Row className="event-types-list-error-row">
              <Message
                className="error-message__style"
                icon="exclamation circle"
                error
                header={t('An error ocurred')}
                list={this.props.nmTrainingNames.state.result.messages.map(o => o.body)}
              />
            </Grid.Row>
          )}
          <Grid.Row className="event-types-list-items-row request-list__table-view">
            <TableView
              /////////////////For build table keyboard navegation/////////////////
              selectable={!this.state.newNMTrainingNameShown && !this.state.changeNMTrainingNameShown}
              maxSelection={1}
              onHideCheckbox={true}
              selectionType={'allRow'}
              onEnterKeydown={this.handleOnEnterKeydown}
              onRowDoubleClick={this.handleOnRowDoubleClick}
              preventEnterKeyDownEvent={someFilterOpened}
              //showActionsConfirmModal={true}
              /////////////////For build table keyboard navegation/////////////////
              model={tableModel}
              extraActions={[
                {
                  content: (
                    <>
                      <Icon name="edit" />
                      &nbsp;{t('Edit')}
                    </>
                  ),
                  onClick: item => {
                    this.setState({ selectedItem: item }, () => this.onEditItem());
                  }
                }
              ]}
              onOrderByChanged={this.handleOrderBy}
              onNewItem={this.onNewItem}
              onRefresh={this.load}
              canEdit={false}
              canDelete={true}
              onDeleteRow={this.onDelete}
              onSaveRow={this.onSaveRow}
              onPageChange={this.handlePageChange}
              onFilterChange={this.handleFilterChange}
              canCreateNew={true}
              createNewButtonTitle={t('Add Training Name')}
            ></TableView>
          </Grid.Row>
        </Grid>
        {(this.state.newNMTrainingNameShown && <NewNMTrainingNameView onClose={this.onNewItemClosed} {...this.props} />) ||
          (this.state.changeNMTrainingNameShown && (
            <ChangeNMTrainingNameView onClose={this.onEditItemClosed} currentDto={this.state.selectedItem} />
          ))}
      </>
    );
  }
}

export default withTranslation()(NMTrainingNameListPage);
